import { FormGroup } from '@angular/forms';

import { SubmissionConstants } from '../../models/constants/submissions-constants';
import {
  FormRule,
  FormRuleEffect,
  FormRuleFactory,
  RuleAction,
} from 'src/app/shared/forms';
import { TokenClaimsService } from 'src/app/shared/services';

export class WaterHeaterRules {
  constructor(
    private ruleFact: FormRuleFactory,
    private submissionsConst: SubmissionConstants,
    private tokenService: TokenClaimsService,
  ) {}

  getV3CustomRules(
    subValues: FormGroup,
    savedWaterHeatingValue?: string,
  ): FormRule[] {
    const isAdmin = this.tokenService.isEnergyCreditAdmin;
    const hasSavedValue = !!savedWaterHeatingValue;

    const isAlternateConstruction = this.isAlternateConstructionSerial(
      subValues.get('homeSerialNumber')?.value,
    );
    const hasEBuiltDesignOption = [
      'originalEBuiltElectric',
      'electricEBuiltPlus',
      'originalEBuiltGas',
      'gasEBuiltPlus',
    ].includes(subValues.get('energyEfficiencyType')?.value);

    if (isAdmin && !isAlternateConstruction && hasEBuiltDesignOption) {
      return [
        this.ruleFact.createRule(`${true}`, [
          this.setWaterHeating(
            RuleAction.SET_WARNING_MESSAGE,
            this.submissionsConst.waterHeatingEBuiltWarning,
          ),
          this.setWaterHeating(RuleAction.MAKE_ENABLED),
        ]),
      ];
    } else if (isAdmin && isAlternateConstruction) {
      return [
        this.ruleFact.createRule(`${true}`, [
          this.setWaterHeating(RuleAction.MAKE_ENABLED),
          this.setWaterHeating(RuleAction.CLEAR_ERROR_MESSAGE),
          this.setWaterHeating(RuleAction.SET_WARNING_MESSAGE, ''),
        ]),
      ];
    } else if (!isAdmin && !isAlternateConstruction && !hasEBuiltDesignOption) {
      return [
        this.ruleFact.createRule(`${true}`, [
          this.setWaterHeating(RuleAction.MAKE_DISABLED),
          this.setWaterHeating(RuleAction.SET_TOOLTIP, ''),
        ]),
      ];
    } else if (!isAdmin && !isAlternateConstruction && hasEBuiltDesignOption) {
      this.setFormError(subValues, 'waterHeatingInvalid');
      return [
        this.ruleFact.createRule(`${true}`, [
          this.setWaterHeating(RuleAction.MAKE_DISABLED),
          this.setWaterHeating(
            RuleAction.SET_ERROR_MESSAGE,
            this.submissionsConst.waterHeatingEBuiltWarning,
          ),
          this.setWaterHeating(RuleAction.MAKE_INVALID),
        ]),
      ];
    } else if (isAlternateConstruction) {
      // Non-admins can freely edit an unset water heater value, but once a
      // value has been saved it locks for them - only an admin can change it.
      if (!isAdmin && hasSavedValue) {
        return [
          this.ruleFact.createRule(`${true}`, [
            this.setWaterHeating(RuleAction.MAKE_DISABLED),
          ]),
        ];
      }
      return [
        this.ruleFact.createRule(`${true}`, [
          this.setWaterHeating(RuleAction.MAKE_ENABLED),
          this.setWaterHeating(RuleAction.CLEAR_ERROR_MESSAGE),
        ]),
      ];
    }
    return [];
  }

  private isAlternateConstructionSerial(serialNumber: string): boolean {
    return (serialNumber ?? '').endsWith('AC');
  }

  private setWaterHeating(action: RuleAction, value?: any): FormRuleEffect {
    return {
      action,
      affectingType: 'control',
      applyTo: 'waterHeating',
      value,
    };
  }

  private setFormError(form: FormGroup, key: string): void {
    const currentErrors = { ...(form.errors ?? {}) };
    form.setErrors({ ...currentErrors, [key]: true });
  }
}
